"use client"

import { trackTelemetry } from "@/lib/utils/telemetry"
import { Button } from "@/components/ui/button"
import { ArrowRight, Download, MessageCircle } from "lucide-react"
import { useLanguage } from "../contexts/language-context"

export default function Hero() {
  const { t, language } = useLanguage()

  const handleViewPortfolio = () => {
    trackTelemetry("cta_click_lab");
    document.getElementById("case-studies")?.scrollIntoView({ behavior: "smooth" })
  }

  const handleWhatsApp = () => {
    trackTelemetry("cta_click_whatsapp");
    window.open("https://wa.me/573216914632", "_blank")
  }

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center relative overflow-hidden pt-0 md:pt-0"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-sky-50/30 dark:to-slate-900/50" />
      <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-sky-500/10 to-purple-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Heading */}
          <div className="mb-8 animate-fade-in">
            <h1 className="font-montserrat text-5xl font-bold leading-[1.7] tracking-[-3.4817px] text-[#0A0A0A] dark:text-white sm:text-6xl md:text-7xl lg:text-[82.939px] mb-6">
              
              {/* Mobile Title with Gradient First Word */}
              <span className="block sm:hidden">
                {(() => {
                  const rawMobile = t("hero.titleMobile") || ""
                  const m = rawMobile.match(/^([^\s]+)([\s\S]*)$/)
                  const first = m ? m[1] : rawMobile
                  const rest = m ? m[2] : ""
                  return (
                    <>
                      <span className="gradient-text">{first}</span>
                      <span>{rest}</span>
                    </>
                  )
                })()}
              </span>

              {/* Desktop Title */}
              <span className="hidden sm:block">
                {(() => {
                  const raw = t("hero.title") || ""
                  const sentences = raw.match(/[^.!?]+[.!?]?/g) || [raw]
                  return (
                    <>
                      {sentences.map((s, i) => {
                        const sentence = s.trim()
                        if (!sentence) return null
                        const m = sentence.match(/^([^\s]+)([\s\S]*)$/)
                        const first = m ? m[1] : sentence
                        const rest = m ? m[2] : ""
                        return (
                          <span key={i}>
                            <span className="gradient-text">{first}</span>
                            <span>{rest}</span>
                            {i < sentences.length - 1 && <br />}
                          </span>
                        )
                      })}
                    </>
                  )
                })()}
              </span>
            </h1>
          </div>

          {/* Description */}
          <p className="text-lg sm:text-xl md:text-2xl text-low-contrast mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in">
            {t("hero.description")}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
            <Button
              size="lg"
              onClick={handleViewPortfolio}
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-semibold text-lg px-8 py-4 focus-ring group"
            >
              {t("hero.viewPortfolio")}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            {/* Link to locale-specific resume PDF. */}
            <a 
              href={language === 'es' ? '/davidxavila_cv_2025_es.pdf' : '/davidxavila_cv_2025_en.pdf'} 
              download 
              aria-label={t("hero.downloadCV")}
              onClick={() => trackTelemetry("cta_download_cv", { locale: language })}
            >
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-4 border-2 border-sky-500/50 hover:border-sky-500 hover:bg-sky-500/10 focus-ring group bg-transparent text-high-contrast"
              >
                <Download className="mr-2 h-5 w-5 group-hover:-translate-y-1 transition-transform" />
                {t("hero.downloadCV")}
              </Button>
            </a>

            <Button
              size="lg"
              variant="outline"
              onClick={handleWhatsApp}
              className="text-lg px-8 py-4 border-2 border-purple-500/50 hover:border-purple-500 hover:bg-purple-500/10 focus-ring group bg-transparent text-high-contrast"
            >
              <MessageCircle className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              {t("hero.whatsapp")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}